import type { GetCaseRecordResponse } from '@compass/client/types/case_record';
import { EmptyState } from '@components/EmptyState';
import { GenericTable } from '@components/GenericTable';
import { QuickFilters } from '@components/QuickFilters';
import { useDebounce } from '@hooks/useDebounce';
import { useStore } from '@store/index';
import { XCircle } from 'lucide-react';
import React, { useMemo, useState } from 'react';

import { ErrorContainer } from '@/components/ErrorContainer';
import { Breadcrumb, BreadcrumbPortal } from '@/components/Header';
import { type CasesTableContext, createCasesTableConfig } from '@/components/TableBuilder';
import { DateRangePicker } from '@/components/ui/date-range-picker';
import { SearchInput } from '@/components/ui/search-input';
import type { FiltersConfig } from '@/config/quickFilters.config';
import { CaseState } from '@/constants/caseStates';
import { WorkflowType } from '@/constants/dispositions';
import { PAGE_SIZE_OPTIONS } from '@/constants/pagination';
import { PAYOR_WORKFLOW_TYPES } from '@/constants/workflows';
import { useAllMyCases } from '@/hooks/useAllMyCases';
import { usePayors } from '@/hooks/usePayors';
import { useWorkflowSlaMap } from '@/hooks/useWorkflowSlaMap';
import { dateToNaiveString, naiveDateToUtc, naiveStringToDate } from '@/utils/date';
import { compareRefillPriority } from '@/utils/refillPriority';
import { buildRoutePath } from '@/utils/routes';
import { buildCaseSlaParams, computeRemainingMinutes } from '@/utils/sla';
import { isValidObjectId } from '@/utils/validation';

const DEFAULT_PAGE_SIZE = 50;

// Tabs in this view. Both tabs share the same date filter; Pending
// further restricts to non-terminal states client-side and sorts refill cases
// by call priority, then the rest by ascending time remaining. Pending = strict
// subset of All.
const TAB_ALL = 'all';
const TAB_PENDING = 'pending';

const MY_CASES_FILTERS: FiltersConfig = {
  filters: [
    {
      id: TAB_PENDING,
      name: 'Pending',
      badgeColor: 'bg-amber-100 text-amber-700',
      filter: {},
    },
    {
      id: TAB_ALL,
      name: 'All',
      badgeColor: 'bg-purple-100 text-purple-700',
      filter: {},
    },
  ],
};

// Terminal states a case is considered "done" in. Excluded from the Pending tab.
// Matches the user-facing definition: completed / terminated / cancelled. `failed`
// is included so broken cases don't pollute the pending queue (no user action
// resolves them — they need admin attention).
const TERMINAL_CASE_STATES = new Set<string>([
  CaseState.COMPLETED,
  CaseState.TERMINATED,
  CaseState.CANCELLED,
  CaseState.FAILED,
]);

const isPendingCase = (item: GetCaseRecordResponse): boolean =>
  !!item.state && !TERMINAL_CASE_STATES.has(item.state);

// Default last 7 days as naive (YYYY-MM-DD) strings.
const getDefaultDateRange = () => {
  const today = new Date();
  const past = new Date();
  past.setDate(today.getDate() - 7);
  return {
    from: dateToNaiveString(past),
    to: dateToNaiveString(today),
  };
};

const MyCasesContainer: React.FC = () => {
  const selectedOrganizationId = useStore(state => state.selectedOrganizationId);
  const getProgramsArray = useStore(state => state.getProgramsArray);
  const getTimeZone = useStore(state => state.getTimeZone);

  const timeZone = getTimeZone();
  const programs = getProgramsArray();

  // Filters
  const [activeQuickFilter, setActiveQuickFilter] = useState<string>(TAB_PENDING);
  const defaultRange = useMemo(() => getDefaultDateRange(), []);
  const [dateFrom, setDateFrom] = useState<string>(defaultRange.from);
  const [dateTo, setDateTo] = useState<string>(defaultRange.to);
  const [searchValue, setSearchValue] = useState<string>('');
  const debouncedSearch = useDebounce(searchValue, 500);

  const isPendingTab = activeQuickFilter === TAB_PENDING;

  // Client-side pagination for the All tab. The API has no states filter, so we
  // fetch up to `useAllMyCases.maxPages × pageSize` rows once and slice locally.
  // This keeps All and Pending counts consistent (same underlying dataset) and
  // lets Pending = strict subset of All.
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);

  // Reset pagination when any filter changes, or when switching tabs.
  React.useEffect(() => {
    setCurrentPage(1);
  }, [dateFrom, dateTo, debouncedSearch, pageSize, activeQuickFilter]);

  // Resolve case-id-style search into ObjectIds (everything else is ignored at the API
  // level because the endpoint only supports case_record_ids; non-ObjectId entries
  // would silently match nothing).
  const caseRecordIds = useMemo(() => {
    if (!debouncedSearch) return undefined;
    const ids = debouncedSearch
      .split(/[,;]/)
      .map(v => v.trim())
      .filter(v => v.length > 0 && isValidObjectId(v));
    return ids.length > 0 ? ids : undefined;
  }, [debouncedSearch]);

  const {
    isLoading,
    error,
    cases: rawCases,
  } = useAllMyCases({
    case_record_ids: caseRecordIds,
    created_after: dateFrom ? naiveDateToUtc(dateFrom, timeZone, 'start') : undefined,
    created_before: dateTo ? naiveDateToUtc(dateTo, timeZone, 'end') : undefined,
  });

  // Cross-program SLA map: every case can come from a different workflow, so SLA
  // minutes must be resolved per-row by workflow_id rather than from a single
  // selectedWorkflow. The hook fans out one workflows-by-program request per
  // program in scope and merges results into a workflowId → minutes map.
  const programIdsForSla = useMemo(
    () => Array.from(new Set(rawCases.map(c => c.program_id).filter((id): id is string => !!id))),
    [rawCases],
  );
  const { slaByWorkflowId, versionByWorkflowId } = useWorkflowSlaMap(programIdsForSla);

  // Payor column visibility & resolution: only fetch payors when the column will
  // actually render — most My Cases rows aren't PharmBV/MedBV.
  const showPayorColumn = useMemo(
    () => rawCases.some(c => !!c.workflow_type && PAYOR_WORKFLOW_TYPES.has(c.workflow_type)),
    [rawCases],
  );
  const { payors } = usePayors(showPayorColumn);
  const payorBySlug = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of payors) {
      if (p.slug && p.name) map.set(p.slug, p.name);
    }
    return map;
  }, [payors]);

  // Pending list = non-terminal cases. Refill cases come first, in their call
  // priority order (JNJ-279, utils/refillPriority.ts). The rest are sorted by
  // least time remaining (most urgent first); cases without enough data to
  // compute remaining time sink to the end so the action-required rows surface
  // at the top. case_record id is used as a stable tie-breaker to prevent row
  // jitter across refetches when multiple rows have equal (or both-missing)
  // remaining time.
  const pendingCases = useMemo(() => {
    const pending = rawCases.filter(isPendingCase);
    const now = new Date();
    const refill = pending
      .filter(c => c.workflow_type === WorkflowType.REFILL)
      .sort((a, b) => compareRefillPriority(a, b, now));
    const others = pending
      .filter(c => c.workflow_type !== WorkflowType.REFILL)
      .sort((a, b) => {
        const ra = computeRemainingMinutes(buildCaseSlaParams(a, slaByWorkflowId));
        const rb = computeRemainingMinutes(buildCaseSlaParams(b, slaByWorkflowId));
        const tieBreak = () => (a.id ?? '').localeCompare(b.id ?? '');
        if (ra === undefined && rb === undefined) return tieBreak();
        if (ra === undefined) return 1;
        if (rb === undefined) return -1;
        if (ra !== rb) return ra - rb;
        return tieBreak();
      });
    return [...refill, ...others];
  }, [rawCases, slaByWorkflowId]);

  // Tab counts come from the same fetched dataset (no separate counts API for
  // cross-program views).
  const tabCounts = useMemo(
    () => ({ All: rawCases.length, Pending: pendingCases.length }),
    [rawCases.length, pendingCases.length],
  );

  // What the table actually renders. All = client-paginated slice of the full
  // fetched set; Pending = full filtered+sorted list (no pagination).
  const displayedCases = useMemo(() => {
    if (isPendingTab) return pendingCases;
    const start = (currentPage - 1) * pageSize;
    return rawCases.slice(start, start + pageSize);
  }, [isPendingTab, pendingCases, rawCases, currentPage, pageSize]);

  const hasNextPage = !isPendingTab && currentPage * pageSize < rawCases.length;

  // Programs map for the Program column.
  const programsById = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of programs) {
      if (p.id) map.set(p.id, p.name || p.id);
    }
    return map;
  }, [programs]);

  const tableContext: CasesTableContext = useMemo(
    () => ({
      programsById,
      slaByWorkflowId,
      versionByWorkflowId,
      payorBySlug,
      showPayerHours: true,
    }),
    [programsById, slaByWorkflowId, versionByWorkflowId, payorBySlug],
  );

  // Default 7-day window is treated as "no filter" when distinguishing the empty
  // states ("no assignments at all" vs "no rows match current filters").
  const isDefaultDateRange = dateFrom === defaultRange.from && dateTo === defaultRange.to;
  const hasActiveFilters = !!debouncedSearch || !isDefaultDateRange || isPendingTab;

  // Custom row link: use each case's own org/program (cases can span programs).
  const tableConfig = useMemo(() => {
    const includeColumns = ['program', 'workflow_type'];
    if (showPayorColumn) includeColumns.push('payor');

    const base = createCasesTableConfig(
      null, // no single selected workflow — cross-program view
      undefined, // no per-workflow column customization
      undefined, // onActionSelect
      undefined, // onAssigneeSelect — read-only, column is excluded
      item => {
        const orgId = item.organization_id || selectedOrganizationId || '';
        const progId = item.program_id || '';
        if (!orgId || !progId || !item.id) return '#';
        return buildRoutePath(`/cases/${item.id}`, orgId, progId);
      },
      undefined,
      undefined, // usersMap — unused: Created by column is excluded
      undefined, // usersLoading — unused: Created by column is excluded
      // Exclude the assignee column (every row is the current user) and the
      // Created by column (not useful in a self-scoped "My Cases" view).
      ['assigned', 'created_by'],
      undefined,
      { includeColumns, context: tableContext },
    );
    return {
      ...base,
      emptyState: hasActiveFilters
        ? {
            title: isPendingTab ? 'No pending cases' : 'No cases match your filters',
            description: isPendingTab
              ? 'Every case assigned to you is completed, cancelled, or terminated.'
              : 'Try adjusting the date range or Case ID search.',
          }
        : {
            title: 'You have no cases assigned to you',
            description: 'Cases assigned to you across this organisation will appear here.',
          },
    };
  }, [showPayorColumn, tableContext, selectedOrganizationId, hasActiveFilters, isPendingTab]);

  const breadcrumb = (
    <BreadcrumbPortal>
      <Breadcrumb>
        <Breadcrumb.Item>My Cases</Breadcrumb.Item>
      </Breadcrumb>
    </BreadcrumbPortal>
  );

  if (!selectedOrganizationId) {
    return (
      <>
        {breadcrumb}
        <EmptyState
          title="No organization selected"
          description="Select an organization to view your cases."
          icon={XCircle}
          iconClassName="text-muted-foreground"
        />
      </>
    );
  }

  if (error) {
    return (
      <>
        {breadcrumb}
        <ErrorContainer errorMessage={error?.message || 'Failed to load cases'} />
      </>
    );
  }

  return (
    <>
      {breadcrumb}
      <div className="flex flex-col h-full min-h-0">
        <div className="flex flex-col flex-1 min-h-0 px-6">
          <div className="flex items-center gap-3 mt-4">
            <SearchInput
              searchOptions={[{ key: 'case_id', label: 'Case ID' }]}
              activeSearchType="case_id"
              searchValues={{ case_id: searchValue }}
              onSearchValueChange={(_type, value) => setSearchValue(value)}
              // Required prop on SearchInput, but no-op here because there is only one search type.
              onSearchTypeChange={() => {}}
              placeholder={() => 'Enter exact Case ID'}
            />
            <DateRangePicker
              initialDateFrom={dateFrom ? naiveStringToDate(dateFrom) : undefined}
              initialDateTo={dateTo ? naiveStringToDate(dateTo) : undefined}
              timezone={timeZone}
              onUpdate={({ range }) => {
                if (!range.from && !range.to) return;
                if (range.from) {
                  setDateFrom(dateToNaiveString(range.from));
                } else {
                  return;
                }
                if (range.to) {
                  setDateTo(dateToNaiveString(range.to));
                } else {
                  setDateTo(dateToNaiveString(range.from));
                }
              }}
              showCompare={false}
            />
          </div>

          <div className="mt-4">
            <QuickFilters
              activeFilter={activeQuickFilter}
              onFilterChange={setActiveQuickFilter}
              filtersConfig={MY_CASES_FILTERS}
              counts={tabCounts}
              countsLoading={isLoading}
            />
          </div>

          <GenericTable
            config={tableConfig}
            data={displayedCases}
            loading={isLoading}
            error={null}
            currentPage={currentPage}
            hasNextPage={hasNextPage}
            onPageChange={setCurrentPage}
            searchTerm={debouncedSearch}
            pageSize={pageSize}
            pageSizeOptions={[...PAGE_SIZE_OPTIONS]}
            onPageSizeChange={setPageSize}
            selectionMode={false}
            hidePagination={isPendingTab}
          />
        </div>
      </div>
    </>
  );
};

export default MyCasesContainer;
